import React, { useState, useEffect } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../services/api";

const FONT = "'Inter', sans-serif";

interface KpiCard {
  label: string;
  subLabel: string;
  value: string;
  valueColor: string;
  navigateTo?: string;
}

interface DashboardKpiCardsProps {
  data?: KpiCard[];
}

const defaultKpiCards: KpiCard[] = [
  {
    label: "On-Time Network Performance",
    subLabel: "This Month (MTD)",
    value: "94.2%",
    valueColor: "#16A34A",
    navigateTo: "/orders?risk=none",
  },
  {
    label: "Total Active Orders",
    subLabel: "This Month (MTD)",
    value: "—",
    valueColor: "#1A1A2E",
    navigateTo: "/orders?status=ACTIVE",
  },
  {
    label: "Total Orders At Risk",
    subLabel: "This Month (MTD)",
    value: "—",
    valueColor: "#EF4444",
    navigateTo: "/orders?risk=at_risk",
  },
];

const ACTIVE_GROUPS = new Set(["PENDING", "NOT_STARTED", "IN_PROGRESS", "AT_RISK", "DELAYED"]);

const DashboardKpiCards: React.FC<DashboardKpiCardsProps> = ({ data }) => {
  const [totalActiveOrders, setTotalActiveOrders] = useState<number | null>(null);
  const [totalAtRisk, setTotalAtRisk] = useState<number | null>(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [onTimePerf, setOnTimePerf] = useState<number | null>(null);
  const [onTimePerfLoading, setOnTimePerfLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    apiService
      .getDatabricksKpis()
      .then((res: { data: { kpis: { label: string; count: number }[]; total: number } }) => {
        const kpis: { label: string; count: number }[] = res.data.kpis ?? [];
        const active = kpis
          .filter((k) => ACTIVE_GROUPS.has(k.label))
          .reduce((sum, k) => sum + k.count, 0);
        const atRiskCount  = kpis.find((k) => k.label === "AT_RISK")?.count  ?? 0;
        const delayedCount = kpis.find((k) => k.label === "DELAYED")?.count  ?? 0;
        const atRisk = atRiskCount + delayedCount;
        setTotalActiveOrders(active);
        setTotalAtRisk(atRisk > 0 ? atRisk : null);
      })
      .catch(() => {
        setTotalActiveOrders(null);
        setTotalAtRisk(null);
      })
      .finally(() => setKpisLoading(false));
  }, []);

  useEffect(() => {
    apiService
      .getOnTimePerformance()
      .then((res: { data: { on_time_count: number; total_count: number; percentage: number } }) => {
        setOnTimePerf(res.data.percentage);
      })
      .catch((err: unknown) => {
        const e = err as { response?: { status: number; data: unknown } };
        console.error("[OnTimePerf] status:", e?.response?.status, "body:", JSON.stringify(e?.response?.data), "err:", err);
        setOnTimePerf(null);
      })
      .finally(() => setOnTimePerfLoading(false));
  }, []);

  const cards = (data || defaultKpiCards).map((card) => {
    if (card.label === "On-Time Network Performance") {
      return {
        ...card,
        value: onTimePerfLoading ? "…" : onTimePerf !== null ? `${onTimePerf}%` : "—",
      };
    }
    if (card.label === "Total Active Orders") {
      return {
        ...card,
        value: kpisLoading ? "…" : totalActiveOrders !== null ? String(totalActiveOrders) : "—",
      };
    }
    if (card.label === "Total Orders At Risk") {
      return {
        ...card,
        value: kpisLoading ? "…" : totalAtRisk !== null ? String(totalAtRisk) : "—",
      };
    }
    return card;
  });

  const handleCardClick = (navigateTo?: string) => {
    if (navigateTo) {
      navigate(navigateTo);
    }
  };

  const handleCardKeyDown = (event: React.KeyboardEvent, navigateTo?: string) => {
    if (navigateTo && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      navigate(navigateTo);
    }
  };

  return (
    <Box>
      {/* Title Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: 0.5 }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: "1.5rem",
              fontWeight: 600,
              color: "#111111",
              lineHeight: 1,
            }}
          >
            Dashboard
          </Typography>
          <Tooltip
            title="This dashboard aggregates real-time supply chain data from your connected ERP systems, freight providers, and customs data feeds."
            arrow
            placement="right"
          >
            <span style={{ cursor: "pointer", color: "#6B7280", lineHeight: 1, display: "flex", alignItems: "center" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </span>
          </Tooltip>
        </Box>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: "0.875rem",
            color: "#6B7280",
            lineHeight: 1.5,
          }}
        >
          Real-time insights into your supply chain operations
        </Typography>
      </Box>

      {/* KPI Cards Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          gap: { xs: 2, md: 3 },
        }}
      >
        {cards.map((card) => (
          <Box
            key={card.label}
            onClick={() => handleCardClick(card.navigateTo)}
            onKeyDown={(e) => handleCardKeyDown(e, card.navigateTo)}
            role={card.navigateTo ? "button" : undefined}
            tabIndex={card.navigateTo ? 0 : undefined}
            aria-label={card.navigateTo ? `${card.label}: ${card.value}. Click to view orders.` : undefined}
            sx={{
              bgcolor: "#FFFFFF",
              borderRadius: "12px",
              border: "1px solid #EAEAEA",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              px: 3,
              py: 2.5,
              height: "100%",
              minHeight: { md: 104 },
              display: "flex",
              flexDirection: "column",
              gap: 1,
              cursor: card.navigateTo ? "pointer" : "default",
              transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
              "&:hover": card.navigateTo
                ? {
                    transform: "scale(1.02)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    borderColor: "#D1D5DB",
                  }
                : {},
              "&:focus-visible": card.navigateTo
                ? {
                    outline: "2px solid #2563EB",
                    outlineOffset: "2px",
                  }
                : {},
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "#111111",
                lineHeight: 1.4,
              }}
            >
              {card.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "0.75rem",
                color: "#6B7280",
              }}
            >
              {card.subLabel}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "1.5rem",
                fontWeight: 700,
                color: card.valueColor,
                lineHeight: 1.2,
                mt: 0.5,
              }}
            >
              {card.value}
            </Typography>
          </Box>
        ))}
      </Box>

    </Box>
  );
};

export default DashboardKpiCards;
